import { createRoute, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Button } from "../components/ui/Button/Button";
import { Badge } from "../components/ui/Badge/Badge";
import { ConfirmDialog } from "../components/ui/ConfirmDialog/ConfirmDialog";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { BackButton } from "../components/ui/BackButton/BackButton";
import { CreateCompanyModal } from "./companies";
import { useLeadDetail, useConvertLead, useDeleteLead } from "../hooks/use-leads";
import type { CreateCompanyInput } from "../hooks/use-companies";
import { useToast } from "../lib/use-toast";
import styles from "./LeadDetail.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/leads/$id",
	component: withPermission(LeadDetailPage, "leads", "read"),
});

function LeadDetailPage() {
	const { id } = route.useParams();
	const { t } = useTranslation();
	const leadQuery = useLeadDetail(id);
	const convertLead = useConvertLead();
	const deleteLead = useDeleteLead();
	const navigate = useNavigate();
	const { showToast } = useToast();
	const [convertOpen, setConvertOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);

	if (leadQuery.isLoading) return <LoadingSpinner />;
	if (leadQuery.isError || !leadQuery.data) return <ErrorFallback onRetry={leadQuery.refetch} />;

	const lead = leadQuery.data;

	return (
		<div>
			<BackButton fallbackTo="/leads" />
			<PageHeader
				title={`${lead.firstName} ${lead.lastName}`}
				action={
					<div className={styles.actions}>
						{lead.status !== "converted" && (
							<Button onClick={() => setConvertOpen(true)}>{t("convert")}</Button>
						)}
						<Button variant="danger" onClick={() => setDeleteOpen(true)}>
							{t("delete")}
						</Button>
					</div>
				}
			/>

			<div className={styles.grid}>
				<Card header={t("generalInfo")}>
					<div className={styles.infoGrid}>
						<Info label={t("firstName")} value={lead.firstName} />
						<Info label={t("lastName")} value={lead.lastName} />
						<Info label={t("email")} value={lead.email} />
						<Info label={t("phone")} value={lead.phone} />
						<Info label={t("companyName")} value={lead.companyName} />
						<Info label={t("tradeName")} value={lead.tradeName} />
						<Info label={t("taxNumber")} value={lead.taxNumber} />
						<Info label={t("taxOffice")} value={lead.taxOffice} />
						<Info label={t("fax")} value={lead.fax} />
						<Info label={t("website")} value={lead.website} />
						<Info label={t("industry")} value={lead.industry} />
						<div>
							<span className={styles.infoLabel}>{t("status")}</span>
							<Badge variant={lead.status === "converted" ? "success" : "primary"}>
								{t(lead.status)}
							</Badge>
						</div>
					</div>
				</Card>
			</div>

			<CreateCompanyModal
				isOpen={convertOpen}
				onClose={() => setConvertOpen(false)}
				title={t("convert")}
				submitLabel={t("convert")}
				convertMode
				initialValues={{
					legalName: lead.companyName || `${lead.firstName} ${lead.lastName}`,
					tradeName: lead.tradeName ?? "",
					taxNumber: lead.taxNumber ?? "",
					taxOffice: lead.taxOffice ?? "",
					phone: lead.phone ?? "",
					fax: lead.fax ?? "",
					email: lead.email ?? "",
					website: lead.website ?? "",
					industry: lead.industry ?? "",
					status: "customer",
					addressStreet: lead.address?.street ?? "",
					addressCity: lead.address?.city ?? "",
					addressState: lead.address?.state ?? "",
					addressPostalCode: lead.address?.postalCode ?? "",
					addressCountry: lead.address?.country ?? "Türkiye",
				}}
				onSubmit={async (values: CreateCompanyInput) => {
					await convertLead.mutateAsync({ id, data: values });
					setConvertOpen(false);
					alert(t("convertSuccess"));
				}}
			/>

			<ConfirmDialog
				isOpen={deleteOpen}
				onClose={() => setDeleteOpen(false)}
				onConfirm={() =>
					deleteLead.mutate(id, {
						onSuccess: () => {
							showToast(t("leadDeleteSuccess"));
							void navigate({
								to: "/leads",
								search: {
									page: 1,
									size: 20,
									status: "",
									search: "",
									createdAtFrom: "",
									createdAtTo: "",
								},
							});
						},
					})
				}
				message={t("confirmDelete")}
				isLoading={deleteLead.isPending}
			/>
		</div>
	);
}

function Info({ label, value }: { label: string; value: string | null }) {
	return (
		<div>
			<span className={styles.infoLabel}>{label}</span>
			<p className={styles.infoValue}>{value || "-"}</p>
		</div>
	);
}
